import type { MatchingLink, NoFeedDestination, UnresolvedLink } from './types.js';
import {
  compactGeo,
  geoJudgeable,
  geoServed,
  linkFacts,
  matchVerdict,
  type Verdict,
} from './match-view.js';
import { shiftDays } from './format.js';

/** Still sending: spend inside the last two days (Airman's rows for a day land with the next sync). */
export const SENDING_DAYS = 2;

/**
 * The Matching page grouped by feed (owner decision 2026-09-16): money
 * belongs to the feed, decisions belong to the destination. One block per
 * feed carries the feed's facts and money once - earned against spent across
 * EVERY destination on it, the total Profit by feed already uses - and one
 * row per destination carries its own spend, a verdict and the one reason
 * when it is not simply ready or confirmed. Pure functions over the read's
 * payload; the page renders what these return.
 */
export interface FeedRow {
  link: MatchingLink;
  verdict: Verdict;
  /** the one line beside the chip; null when the row is Ready or Confirmed */
  reason: string | null;
  /** "until 2026-09-15": a window that has closed but still spent inside the last 7 days */
  note: string | null;
  /** the feed the destination's URL names when it contradicts the channel - offered as "Link to …" */
  urlFeed: string | null;
  /** compact geo with the served count for a multi-country destination */
  geo: string;
}

export interface FeedGroup {
  key: string;
  feedId: string;
  accountId: string | null;
  /** the source's own name: the provider, or the account's label for a sheet/upload/inbox */
  source: string;
  rep: string | null;
  /** the feed's type, else Airman's network as the suggestion */
  type: string | null;
  feedGeo: string | null;
  feedGeoCount: number;
  earned7d: number;
  /** Airman spend over the last 7 days attributed to the feed: every destination row shown, plus the
   * days of windows that closed inside the week (the P&L's figure, and the header's - 2026-09-18) */
  spent7d: number;
  /** the part of spent7d on rows that are only suggested */
  spentSuggested7d: number;
  /** the part of spent7d on windows that closed: folded rows, their spend still the feed's */
  spentClosed7d: number;
  net: number;
  lastDay: string | null;
  /** the last day any destination row spent */
  lastSpendDay: string | null;
  /** spend inside the last two days: the feed is still being sent traffic */
  sending: boolean;
  /** still sending and earning nothing in the window: the alert */
  noRevenue: boolean;
  /** still sending, earning something, and less than it spends: the account manager's alert (owner ask 2026-09-18) */
  losing: boolean;
  /** net as a share of the spend, in whole percent; null with no spend */
  margin: number | null;
  /** a confirmed row with a warning (geo, URL, no stats): worth a look */
  warned: boolean;
  /** losing, no revenue or warned: the Flagged page's list */
  flagged: boolean;
  /** a non-USD row in the window with no rate yet: no verdict until it is rated */
  unrated: boolean;
  /** the part of spent7d on confirmed links, live and closed: what the verdicts are judged on */
  spentConfirmed7d: number;
  /** earned minus confirmed spend: the figure the header's Flagged count and the digest judge */
  netConfirmed: number;
  /** stopped sending and its last days earned nothing: a note, never an alert (owner note 2026-09-18) */
  stoppedQuiet: boolean;
  /** suggested rows first (by verdict), then confirmed (by verdict); ids keep the order inside a group */
  rows: FeedRow[];
  /** windows that closed with no spend inside 7 days: history, folded */
  closed: MatchingLink[];
  toConfirm: number;
  toCheck: number;
  /** anything unconfirmed, or confirmed with a warning: the block belongs under Needs a decision */
  needsDecision: boolean;
  /** nothing but closed links: listed once, folded, at the end of Matched */
  historyOnly: boolean;
}

const closedBy = (l: MatchingLink, today: string): boolean =>
  l.validTo != null && l.validTo < today;

/** The source's name on the block: a mechanism account is its own label, never the mechanism word. */
export function sourceName(l: {
  provider: string | null;
  accountLabel: string | null;
  accountProviderLabel: string | null;
  feedProvider: string;
}): string {
  // An inbox account is named "Email reports from <domain>"; the provider the
  // report came from (its provider label) is the name a person knows.
  if (l.provider === 'email') return l.accountProviderLabel || l.accountLabel || l.feedProvider;
  const mechanism = l.provider != null && ['generic', 'upload'].includes(l.provider);
  if (mechanism) return l.accountLabel || l.accountProviderLabel || l.provider || l.feedProvider;
  return l.provider ?? l.feedProvider;
}

/** The one line beside a verdict chip that is not Ready or Confirmed. */
export function reasonFor(l: MatchingLink, v: Verdict): string | null {
  switch (v.label) {
    case 'Channel ≠ URL': {
      const said =
        l.urlFeedId ?? (l.urlIds.length ? l.urlIds.map((i) => i.value).join(' / ') : '?');
      return `URL says ${said}; the channel is ${l.channel ?? '?'}`;
    }
    case 'Geo differs': {
      const g = geoServed(l.geo, l.geoMix, l.geoChecks);
      const primary = l.geo ?? l.geoMix?.[0]?.geo ?? null;
      if (primary && g.missing.includes(primary)) return `primary ${primary} not served`;
      const which =
        g.missing.length > 3
          ? `${g.missing.length} of ${g.declared} countries`
          : g.missing.join(', ');
      return `${which} not served (${Math.round(g.missingShare * 100)}% of the split)`;
    }
    case 'No stats yet':
      return 'no stats rows for this feed yet';
    case 'Owner differs':
      return `owner ${l.ownerRep ?? l.owner ?? '?'}, account ${l.rep ?? '?'}`;
    case 'Feed differs':
      return `pushed as ${l.feedProvider}, the account is ${l.provider ?? 'unknown'}`;
    case 'No account':
      return 'no account';
    default:
      return null;
  }
}

/** Compact geo, with "n of m served" for a multi-country destination once the feed has stats. */
export function rowGeo(l: {
  geo: string | null;
  geoMix: MatchingLink['geoMix'];
  geoChecks: MatchingLink['geoChecks'];
  feedTotalSearches: number;
  feedGeo: string | null;
}): string {
  const base = compactGeo(l.geo, l.geoMix);
  if (l.geoChecks.length > 1 && geoJudgeable(l)) {
    const g = geoServed(l.geo, l.geoMix, l.geoChecks);
    return `${base} · ${g.served} of ${g.declared} served`;
  }
  return base;
}

function toRow(l: MatchingLink, today: string): FeedRow {
  const judged = matchVerdict(linkFacts(l));
  // A confirmed link whose only fault is an error a person had to override to
  // confirm (the owner, the provider) is a decision already made: it reads
  // Confirmed with the fact beside it, and settles its block. A warning on a
  // confirmed link (the geo, no stats, the URL) is live information and keeps
  // the block under Needs a decision.
  const overridden = l.status === 'confirmed' && judged.tone === 'error';
  const verdict: Verdict = overridden ? { label: 'Confirmed', tone: 'ok', order: 0 } : judged;
  const reason = reasonFor(l, judged);
  return {
    link: l,
    verdict,
    reason:
      overridden && reason ? `${reason} (linked anyway)` : verdict.tone === 'ok' ? null : reason,
    note: closedBy(l, today) ? `until ${l.validTo}` : null,
    urlFeed: l.urlAgrees === false && l.urlFeedId && l.urlFeedId !== l.feedId ? l.urlFeedId : null,
    geo: rowGeo(l),
  };
}

const rowOrder = (a: FeedRow, b: FeedRow): number =>
  Number(a.link.status === 'confirmed') - Number(b.link.status === 'confirmed') ||
  a.verdict.order - b.verdict.order ||
  a.link.id - b.link.id;

const groupOrder = (a: FeedGroup, b: FeedGroup): number =>
  Number(b.needsDecision) - Number(a.needsDecision) ||
  b.spent7d - a.spent7d ||
  a.feedId.localeCompare(b.feedId) ||
  (a.accountId ?? '').localeCompare(b.accountId ?? '');

/**
 * Every link folded into feed blocks. A confirmed window that closed is
 * history the day it closes: the spend of its days stays attributed and
 * there is nothing left to decide, so a matched feed that stopped leaves the
 * page (owner question 2026-09-18). A suggested window that closed still
 * shows while it spent inside 7 days: that spend is unattributed until
 * someone confirms or rejects it.
 */
export function groupLinks(links: MatchingLink[], today: string): FeedGroup[] {
  const groups = new Map<string, FeedGroup>();
  // A destination with a live link in a block: its closed windows there are
  // history even while they spent inside 7 days, or the destination would
  // read twice (live, 2026-09-17: the same destination on two rows).
  const live = new Set(
    links
      .filter((l) => l.status !== 'rejected' && !closedBy(l, today))
      .map((l) => `${l.accountId ?? ''}\u0000${l.feedId}\u0000${l.destinationId}`),
  );
  for (const l of links) {
    if (l.status === 'rejected') continue;
    const key = `${l.accountId ?? ''}\u0000${l.feedId}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        feedId: l.feedId,
        accountId: l.accountId,
        source: sourceName(l),
        rep: l.rep,
        type: l.feedType ?? l.network ?? null,
        feedGeo: l.feedGeo,
        feedGeoCount: l.feedGeoCount,
        earned7d: 0,
        spent7d: 0,
        spentSuggested7d: 0,
        spentClosed7d: 0,
        net: 0,
        lastDay: l.feedLastDay,
        lastSpendDay: null,
        sending: false,
        noRevenue: false,
        losing: false,
        margin: null,
        warned: false,
        flagged: false,
        unrated: false,
        spentConfirmed7d: 0,
        netConfirmed: 0,
        stoppedQuiet: false,
        rows: [],
        closed: [],
        toConfirm: 0,
        toCheck: 0,
        needsDecision: false,
        historyOnly: false,
      };
      groups.set(key, g);
    }
    g.earned7d = Math.max(g.earned7d, l.payout7d);
    if (l.feedLastDay && (!g.lastDay || l.feedLastDay > g.lastDay)) g.lastDay = l.feedLastDay;
    // Still being sent traffic is judged on confirmed links, as the header is (review 2026-09-18).
    if (
      l.status === 'confirmed' &&
      l.buyLastDay &&
      (!g.lastSpendDay || l.buyLastDay > g.lastSpendDay)
    )
      g.lastSpendDay = l.buyLastDay;
    if (
      closedBy(l, today) &&
      (l.status === 'confirmed' || !(l.buyCost7d > 0) || live.has(`${key}\u0000${l.destinationId}`))
    ) {
      g.closed.push(l);
      continue;
    }
    g.rows.push(toRow(l, today));
  }
  const out: FeedGroup[] = [];
  for (const g of groups.values()) {
    g.rows.sort(rowOrder);
    g.closed.sort((a, b) => (b.validTo ?? '').localeCompare(a.validTo ?? '') || b.id - a.id);
    // A window that closed inside the week still spent the feed's money on its
    // days (each link's spend is its own days' since 2026-09-18): the block's
    // net is the feed's 7-day net, the same figure the header's Flagged count
    // and the digest judge - a losing feed reads losing on both.
    g.spentClosed7d = g.closed.reduce((n, l) => n + l.buyCost7d, 0);
    g.spent7d = g.rows.reduce((n, r) => n + r.link.buyCost7d, 0) + g.spentClosed7d;
    g.spentSuggested7d = g.rows
      .filter((r) => r.link.status !== 'confirmed')
      .reduce((n, r) => n + r.link.buyCost7d, 0);
    // The verdicts - no revenue, losing, stopped - are judged on CONFIRMED spend
    // only, as the header's Flagged count and the digest are (matching/cost.ts
    // ownedBy(true)): a suggestion's spend is listed as unconfirmed, never held
    // against the feed (review 2026-09-18). The money line still shows it all.
    g.spentConfirmed7d =
      g.rows
        .filter((r) => r.link.status === 'confirmed')
        .reduce((n, r) => n + r.link.buyCost7d, 0) +
      g.closed.filter((l) => l.status === 'confirmed').reduce((n, l) => n + l.buyCost7d, 0);
    g.net = g.earned7d - g.spent7d;
    g.netConfirmed = g.earned7d - g.spentConfirmed7d;
    // A non-USD row with no rate yet counts $0 in earned7d: the feed is neither
    // losing nor without revenue until it is rated (review 2026-09-18).
    g.unrated =
      g.rows.some((r) => r.link.fxMissing7d > 0) || g.closed.some((l) => l.fxMissing7d > 0);
    g.sending = g.lastSpendDay != null && g.lastSpendDay >= shiftDays(today, -SENDING_DAYS);
    g.noRevenue = !g.unrated && g.earned7d <= 0 && g.spentConfirmed7d > 0 && g.sending;
    g.stoppedQuiet = !g.unrated && g.earned7d <= 0 && g.spentConfirmed7d > 0 && !g.sending;
    // Losing: earning something, less than it spends, and still being sent
    // traffic (owner ask 2026-09-18: find and turn off what is unprofitable).
    // A feed that stopped is history, like the no-revenue case.
    g.losing =
      !g.unrated && g.earned7d > 0 && g.spentConfirmed7d > 0 && g.netConfirmed < 0 && g.sending;
    g.margin = g.spent7d > 0 ? Math.round((g.net / g.spent7d) * 100) : null;
    g.toConfirm = g.rows.filter((r) => r.link.status !== 'confirmed').length;
    g.toCheck = g.rows.filter((r) => r.verdict.tone !== 'ok').length;
    g.warned = g.rows.some((r) => r.link.status === 'confirmed' && r.verdict.tone !== 'ok');
    // A feed spending with no revenue, or losing money, is the most actionable
    // thing on the page whatever its links say (owner asks 2026-09-17/18).
    g.needsDecision =
      g.rows.some((r) => r.link.status !== 'confirmed' || r.verdict.tone !== 'ok') ||
      g.noRevenue ||
      g.losing;
    g.flagged = g.losing || g.noRevenue || g.warned;
    g.historyOnly = g.rows.length === 0;
    // A block of nothing but history stays out of both lists: its links are still reachable
    // through the feed's other block, or the closed fold of a live one, never lost - but a
    // block with only closed links and no live row has nothing to decide or show.
    if (g.rows.length || g.closed.length) out.push(g);
  }
  return out.sort(groupOrder);
}

/** A block of open registrations sharing one feed number no account carries yet. */
export interface RegistrationGroup {
  key: string;
  feedId: string;
  feedProvider: string;
  /** the registrations, biggest spend first */
  rows: UnresolvedLink[];
  spent14d: number;
  candidates: number;
}

/**
 * The number a registration is matched on: Airman's channel as it is NOW when
 * it was corrected since the push (live 2026-09-18: pushed as "search", the
 * channel now 388372, and the block read "feed search"), else the number pushed.
 */
export function registrationNumber(u: Pick<UnresolvedLink, 'feedId' | 'channel'>): string {
  return u.channel && u.channel !== u.feedId ? u.channel : u.feedId;
}

/** Open registrations folded by the feed number they wait on, biggest spend first. */
export function groupRegistrations(open: UnresolvedLink[]): RegistrationGroup[] {
  const groups = new Map<string, RegistrationGroup>();
  for (const u of open) {
    const key = `${u.feedProvider.toLowerCase()}\u0000${registrationNumber(u)}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        feedId: registrationNumber(u),
        feedProvider: u.feedProvider,
        rows: [],
        spent14d: 0,
        candidates: 0,
      };
      groups.set(key, g);
    }
    g.rows.push(u);
    g.spent14d += u.buyCost14d;
    g.candidates = Math.max(g.candidates, u.suggestions.length);
  }
  const out = [...groups.values()];
  for (const g of out) g.rows.sort((a, b) => b.buyCost14d - a.buyCost14d || a.id - b.id);
  return out.sort((a, b) => b.spent14d - a.spent14d || a.feedId.localeCompare(b.feedId));
}

/** The registrations and feed-less destinations the page lists: only those with Airman spend in 14 days (owner ask 2026-09-15). */
export function spendingOnly<T extends { buyCost14d: number }>(items: T[]): T[] {
  return items.filter((i) => i.buyCost14d > 0);
}

/**
 * A number the destination's NAME carries that is not its channel - the typo
 * hint (live: "David - CSE - Chrome - WW - 5k PN1016" with channel PN106).
 * Only when the name carries exactly one id-like token and it differs.
 */
export function nameSaysOther(name: string | null, channel: string | null): string | null {
  if (!name || !channel) return null;
  const digits = (v: string) => v.replace(/\D+/g, '');
  const tokens = [
    ...new Set((name.match(/[A-Za-z]{0,4}\d{3,}/g) ?? []).filter((t) => digits(t).length >= 3)),
  ];
  if (tokens.length !== 1) return null;
  const t = tokens[0]!;
  return t.toLowerCase() === channel.toLowerCase() || digits(t) === digits(channel) ? null : t;
}

/** What to do about a registration no source carries, in one line. */
export function registrationHint(u: UnresolvedLink): string | null {
  if (linkableRegistration(u)) return null;
  if (u.suggestions.length) {
    // Only other reps' accounts carry the number: one provider issues it to several people, and the
    // owner's own source is what is missing (owner note 2026-09-18: 388389, Loki and TrafficGurus).
    const others = [...new Set(u.suggestions.map((s) => s.rep))].join(', ');
    return `no source of ${u.ownerRep ?? 'the owner'}'s reports ${registrationNumber(u)} - ${others}'s does: add ${u.ownerRep ?? 'the owner'}'s own source under Configuration → Add stats, or Link to theirs anyway (you will be asked to confirm the cross-owner link)`;
  }
  const typo = nameSaysOther(u.destinationName, u.channel ?? u.feedId);
  if (typo) return `the name says ${typo} - check the channel in Airman`;
  return `no stats source reports ${registrationNumber(u)} yet - add one under Configuration → Add stats, or correct the channel in Airman`;
}

/** A registration a click can match here: a candidate under the owner's own rep, or - with no placeable owner - the one candidate there is. The rest wait on a source. */
export function linkableRegistration(u: UnresolvedLink): boolean {
  return u.suggestions.some((s) => s.sameRep || s.confidence === 'high');
}

/** Every suggested link across the blocks that a glance could confirm: exactly the rows showing Ready. */
export function readyRows(groups: FeedGroup[]): MatchingLink[] {
  return groups.flatMap((g) =>
    g.rows.filter((r) => r.verdict.label === 'Ready').map((r) => r.link),
  );
}

/** How a feed-less destination reads on its row. */
export function noFeedReason(d: NoFeedDestination): string {
  if (d.reason === 'closed')
    return d.status === 'active'
      ? `running with no open link since its last link ended${d.linksEnded ? ` on ${d.linksEnded}` : ''} - give it a channel in Airman, or claim one under Unclaimed feeds`
      : `its links ended${d.linksEnded ? ` on ${d.linksEnded}` : ''} - claim its feed under Unclaimed feeds if it runs again`;
  if (d.reason === 'unlinked')
    return 'its links were all unlinked - claim its feed under Unclaimed feeds, or wait for a new registration';
  if (d.urlIds.length && !d.urlFeedId)
    return `no channel in Airman, and the URL carries only ${d.urlIds.map((i) => i.name).join(', ')} - configure its feed in Airman, or claim one under Unclaimed feeds`;
  return 'no feed configured in Airman - give it a channel there, or claim one under Unclaimed feeds';
}
